import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Card, Button, Spinner, Alert, Form, Modal } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";
import { Link } from "react-router-dom";

export default function AssignProductsToStockPage() {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [products, setProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [stockKeepers, setStockKeepers] = useState([]);
    const [selectedStockKeeperId, setSelectedStockKeeperId] = useState("");
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [handoverImage, setHandoverImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
        loadPageData();
    }, []);

    const loadPageData = async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([fetchInitialData(), fetchProducts()]);
        } catch (err) {
            console.error("Error loading page data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    const fetchInitialData = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/checker/products/initial-data`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data.success) {
                setStockKeepers(
                    response.data?.data?.stock_keepers ||
                    response.data?.data?.stockKeepers ||
                    []
                );
            } else {
                setError(response.data.message || "Unable to load initial data.");
            }
        } catch (err) {
            console.error("Error loading initial data:", err);
            setError(err.response?.data?.message || "Failed to load initial data.");
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/checker/checked-products`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data.success) {
                setProducts(response.data?.data);
            } else {
                setError(response.data.message || "Unable to load products.");
            }
        } catch (err) {
            console.error("Error loading products:", err);
            setError(err.response?.data?.message || "Failed to load products.");
        }
    };

    const toggleProduct = (productId) => {
        setSelectedProducts((prev) => {
            if (prev.includes(productId)) {
                return prev.filter((id) => id !== productId);
            }
            return [...prev, productId];
        });
    };

    const selectAllProducts = () => {
        const filteredIds = filteredProducts.map((product) => product.id);
        setSelectedProducts((prev) => Array.from(new Set([...prev, ...filteredIds])));
    };

    const clearSelectedProducts = () => {
        setSelectedProducts([]);
    };

    const filteredProducts = products.filter((product) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const code = product.product_code ? product.product_code.toLowerCase() : "";
        const title = product.title ? product.title.toLowerCase() : "";
        return code.includes(query) || title.includes(query);
    });

    const openCamera = async () => {
        try {
            setError(null);
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert("Camera is not supported by this browser.");
                return;
            }
            stopCamera();
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: false,
            });
            setCameraStream(stream);
            setCameraOpen(true);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch((err) => {
                        console.error("Video play error:", err);
                    });
                }
            }, 100);
        } catch (err) {
            console.error("Camera error:", err);
            if (err.name === "NotAllowedError") {
                alert("Camera permission was denied. Please allow camera access.");
            } else if (err.name === "NotFoundError") {
                alert("No camera was found on this device.");
            } else {
                alert("Unable to open camera.");
            }
        }
    };

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => {
                track.stop();
            });
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraStream(null);
        setCameraOpen(false);
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        if (!video) return;
        if (!video.videoWidth || !video.videoHeight) {
            alert("Camera is not ready. Please wait.");
            return;
        }
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    alert("Unable to capture photo.");
                    return;
                }
                const file = new File([blob], `stock-handover-${Date.now()}.jpg`, {
                    type: "image/jpeg",
                });
                setHandoverImage(file);
                setImagePreview(URL.createObjectURL(file));
                stopCamera();
            },
            "image/jpeg",
            0.9
        );
    };

    const removeHandoverImage = () => {
        setHandoverImage(null);
        setImagePreview(null);
    };

    const handleAssignProducts = async () => {
        setError(null);
        setSuccess(null);

        if (!selectedStockKeeperId) {
            setError("Please select a stock keeper.");
            return;
        }

        if (selectedProducts.length === 0) {
            setError("Please select at least one product.");
            return;
        }

        if (!handoverImage) {
            setError("Please take a handover photo before assigning the products.");
            return;
        }

        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("stock_keeper_id", selectedStockKeeperId);
            selectedProducts.forEach((productId) => {
                formData.append("product_ids[]", productId);
            });
            formData.append("handover_image", handoverImage);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/checker/products/assign-to-stock-keeper`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data.success) {
                setSuccess(response.data.message || "Products assigned successfully.");
                setSelectedProducts([]);
                setSelectedStockKeeperId("");
                removeHandoverImage();
                await fetchProducts();
            } else {
                setError(response.data.message || "Unable to assign products.");
            }
        } catch (err) {
            console.error("Assignment error:", err);
            if (err.response?.status === 422) {
                const validationErrors = err.response.data.errors;
                if (validationErrors) {
                    const messages = Object.values(validationErrors).flat().join(" ");
                    setError(messages);
                } else {
                    setError(err.response.data.message || "Validation failed.");
                }
            } else {
                setError(err.response?.data?.message || "Failed to assign products.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-3">Loading initial data...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Give Products to Stock Keeper</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Home</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Products</li>
                                <li className="mc-breadcrumb-item">Give to Stock Keeper</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}
                </Col>

                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                            <div style={{ minWidth: "280px", flex: "1 1 300px" }}>
                                <Form.Group className="mb-0">
                                    <Form.Label className="fw-semibold mb-1">Select Stock Keeper</Form.Label>
                                    <Form.Select
                                        value={selectedStockKeeperId}
                                        onChange={(e) => setSelectedStockKeeperId(e.target.value)}
                                    >
                                        <option value="">Choose Stock Keeper...</option>
                                        {stockKeepers.map((sk) => (
                                            <option key={sk.id} value={sk.id}>
                                                {sk.name} {sk.email ? `(${sk.email})` : ""}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                            <div style={{ minWidth: "280px", flex: "1 1 350px" }}>
                                <Form.Group className="mb-0">
                                    <Form.Label className="fw-semibold mb-1">Search Products</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by product code or title..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                            <div>
                                <h4 className="mb-1">Products</h4>
                                <small className="text-muted">
                                    {" — "} {filteredProducts.length} of {products.length} products shown
                                </small>
                            </div>
                            <div>
                                <Button variant="primary" size="sm" className="me-2" onClick={selectAllProducts}>
                                    Select All Filtered
                                </Button>
                                <Button variant="secondary" className="me-2" size="sm" onClick={clearSelectedProducts}>
                                    Clear
                                </Button>
                            </div>
                        </div>

                        {filteredProducts.length === 0 ? (
                            <Alert variant="info">No products found matching your search.</Alert>
                        ) : (
                            <Row>
                                {filteredProducts.map((product) => {
                                    const selected = selectedProducts.includes(product.id);
                                    return (
                                        <Col key={product.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                                            <Card
                                                className={selected ? "h-100 border-primary shadow-sm" : "h-100 shadow-sm"}
                                                style={{ cursor: "pointer", borderWidth: selected ? "2px" : "1px" }}
                                                onClick={() => toggleProduct(product.id)}
                                            >
                                                <div
                                                    style={{
                                                        height: "180px",
                                                        background: "#f8f9fa",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {product.card_img ? (
                                                        <img
                                                            src={process.env.REACT_APP_IMG_URL + product.card_img}
                                                            alt={product.title}
                                                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted">No Image</span>
                                                    )}
                                                </div>
                                                <Card.Body>
                                                    <div className="d-flex align-items-start">
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={() => toggleProduct(product.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="me-2"
                                                        />
                                                        <div>
                                                            <h6 className="mb-1">{product.title}</h6>
                                                            <small className="text-muted">{product.product_code}</small>
                                                        </div>
                                                    </div>
                                                    <div className="mt-3">
                                                        <small>Category: <strong>{product.category?.name}</strong></small>
                                                        <br />
                                                        <small>Selling Style: <strong>{product.selling_style}</strong></small>
                                                        <br />
                                                        <strong className="d-block mt-2">${product.sell_price_dollar}</strong>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                    </div>
                </Col>

                {selectedProducts.length > 0 && (
                    <Col xl={12}>
                        <div className="mc-card mb-4">
                            <h4 className="mb-1">Product Handover</h4>
                            <p className="text-muted mb-4">Take a photo as proof when handing these products to the Stock Keeper.</p>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <div style={{ border: "2px dashed #ddd", borderRadius: "10px", padding: "20px", textAlign: "center" }}>
                                        {imagePreview ? (
                                            <>
                                                <img
                                                    src={imagePreview}
                                                    alt="Handover"
                                                    style={{ width: "100%", maxHeight: "350px", objectFit: "contain", borderRadius: "8px" }}
                                                />
                                                <div className="mt-3">
                                                    <Button variant="danger" size="sm" className="me-2" onClick={removeHandoverImage}>
                                                        Remove Photo
                                                    </Button>
                                                    <Button variant="dark" size="sm" onClick={openCamera}>
                                                        Retake Photo
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: "50px", marginBottom: "15px" }}>📷</div>
                                                <h5>Handover Photo</h5>
                                                <p className="text-muted">Take a photo of the product handover</p>
                                                <Button variant="primary" className="me-2" onClick={openCamera}>
                                                    📷 Take Photo
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div style={{ background: "#f8f9fa", borderRadius: "10px", padding: "20px" }}>
                                        <h5>Handover Summary</h5>
                                        <hr />
                                        <div className="mb-3">
                                            <small className="text-muted">Stock Keeper</small>
                                            <h6>
                                                {stockKeepers.find((sk) => String(sk.id) === String(selectedStockKeeperId))?.name || (
                                                    <span className="text-danger">Not selected</span>
                                                )}
                                            </h6>
                                        </div>
                                        <div className="mb-3">
                                            <small className="text-muted">Selected Products</small>
                                            <h3>{selectedProducts.length}</h3>
                                        </div>
                                        <div className="mb-3">
                                            <small className="text-muted">Handover Photo</small>
                                            <h6>{handoverImage ? "✓ Photo captured" : "⚠ Photo required"}</h6>
                                        </div>
                                        <Button
                                            variant="success"
                                            className="w-100"
                                            size="lg"
                                            disabled={submitting || !handoverImage || !selectedStockKeeperId}
                                            onClick={handleAssignProducts}
                                        >
                                            {submitting ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Assigning...
                                                </>
                                            ) : (
                                                <>✓ Give Products to Stock Keeper</>
                                            )}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                )}
            </Row>

            <Modal show={cameraOpen} onHide={stopCamera} centered size="lg" backdrop="static">
                <Modal.Header closeButton closeVariant="white" style={{ background: "#000", color: "#fff" }}>
                    <Modal.Title>Take Handover Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ background: "#000", padding: 0 }}>
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ width: "100%", maxHeight: "70vh", objectFit: "contain", display: "block" }}
                    />
                </Modal.Body>
                <Modal.Footer style={{ background: "#000" }}>
                    <Button variant="secondary" onClick={stopCamera}>Cancel</Button>
                    <Button variant="primary" onClick={capturePhoto}>📸 Capture Photo</Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}